import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeftRight, ArrowRight, Loader2, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api-client';
import { addressLine } from '@/components/dmp/DmpConsolePanel';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import {
  swapBusNumbers, fetchMappedBusNumbers, fetchBusNumberSwaps, type MappedBusNumber, type BusNumberSwapRecord,
} from '@/hooks/useAddressValidation';
import type { DmpLookupResponse } from '@uniticket/shared';

/**
 * Exchange two DMP addresses' box numbers (user ask 2026-09-01). Admins only; the server gates the route
 * on SYSTEM_ADMIN and locks BOTH jvids regardless of what this screen offers.
 *
 * Why a dedicated action rather than two visits to "Modifier l'adresse": the pair cannot be corrected
 * one row at a time. Moving A onto B's box makes A a seven-field twin of B, so the FIRST edit is
 * refused with a 409 — for a state that only ever exists mid-correction. The server checks uniqueness
 * on the final state and writes both rows in one transaction.
 *
 * The screen proposes, the admin validates: side A is the address the console already has loaded, the
 * admin types the other jvid, and the swap is computed and shown — there is no box number to retype,
 * which is the whole point (a retyped number is a number one can get wrong).
 *
 * PORTALLED TO document.body, and that is load-bearing: the console panel lives inside a GlassCard whose
 * `backdrop-filter` makes it the containing block of `position: fixed` descendants, so an overlay born
 * in that subtree covers the CARD and centres itself below the fold (incident 2026-08-11). This modal is
 * rendered from the page, outside the card, and portalled on top of that.
 */

/**
 * One-line address, box excluded — the box is what the table below is about, so it is shown apart.
 *
 * Reuses the console's own `addressLine`: the DMP's `house_number_extension` is the COMPLETE number,
 * suffix included ("22" → "22", "22" → "22A" — measured: never empty, equal to house_number on 92.4 %
 * of the 1 987 286 live addresses). Concatenating the two would render number 43 as "4343".
 */
function addressOf(a: DmpLookupResponse['reference']): string {
  return `${addressLine(a.streetName, a.houseNumber, a.houseNumberExtension)}, ${a.postalCode} ${a.city}`;
}

/** Same building? Compared on what an admin can see, not on belmap_building_id: two rows of the same
 *  building can carry different building ids precisely when the data is wrong, and this warning exists
 *  to be readable, not to be authoritative. */
function sameBuilding(a: DmpLookupResponse['reference'], b: DmpLookupResponse['reference']): boolean {
  const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
  return norm(a.streetName) === norm(b.streetName)
    && norm(a.houseNumber) === norm(b.houseNumber)
    && norm(a.postalCode) === norm(b.postalCode);
}

const box = (v: string | null | undefined) => (v?.trim() ? v.trim() : '—');

export function SwapBusNumberModal({
  address,
  onClose,
  onSwapped,
}: {
  address: DmpLookupResponse;
  onClose: () => void;
  onSwapped: (jvid: number) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();
  const a = address.reference;

  const [input, setInput] = useState('');
  const [other, setOther] = useState<DmpLookupResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapped, setMapped] = useState<MappedBusNumber[]>([]);
  // Earlier swaps of THIS pair, newest first. null = the history could not be read — kept apart from []
  // (never swapped) so the screen never says "never swapped" when it simply could not check.
  const [pastSwaps, setPastSwaps] = useState<BusNumberSwapRecord[] | null>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const loadOther = useCallback(async () => {
    const jvid = Number(input.trim());
    if (!Number.isInteger(jvid) || jvid <= 0) return;
    if (jvid === a.jvid) { setError(t('tickets:dmpLookup.swap.sameJvid')); return; }
    setLoading(true);
    setError(null);
    setOther(null);
    setMapped([]);
    setPastSwaps([]);
    try {
      // The address and the pair's swap history land TOGETHER (user ask 2026-09-15): a warning arriving a
      // moment after the table would leave a window where the button is live and the screen says nothing.
      const [res, swaps] = await Promise.all([
        apiFetch<{ data: DmpLookupResponse }>(`/dmp/address/${jvid}`),
        fetchBusNumberSwaps(a.jvid, jvid).then((r) => r.data, () => null),
      ]);
      setPastSwaps(swaps);
      setOther(res.data);
      // Best-effort and deliberately not awaited into the error path: what the operators call these
      // boxes informs the decision, it never gates it.
      try {
        setMapped((await fetchMappedBusNumbers([a.jvid, jvid])).data);
      } catch { /* the swap preview stands without it */ }
    } catch (err) {
      setError((err as Error)?.message || t('tickets:dmpLookup.error'));
    } finally {
      setLoading(false);
    }
  }, [input, a.jvid, t]);

  const b = other?.reference ?? null;
  const identicalBoxes = b !== null && (a.busNumber ?? '').trim() === (b.busNumber ?? '').trim();
  const differentBuilding = b !== null && !sameBuilding(a, b);
  const connected = address.attributes?.status === 'HOMES_CONNECT'
    || other?.attributes?.status === 'HOMES_CONNECT';
  const canSwap = b !== null && !identicalBoxes && !saving;
  const lastSwap = pastSwaps?.[0] ?? null;
  // Said only when it is TRUE: the next swap puts back exactly what the last one replaced, i.e. neither box
  // has moved since. If one did, the history is still shown, but "you are undoing it" would be a guess.
  const undoesLastSwap = b !== null && lastSwap !== null
    && (a.busNumber ?? '').trim() === lastSwap.aTo
    && (b.busNumber ?? '').trim() === lastSwap.aFrom;

  const submit = async () => {
    if (!b || !canSwap) return;
    if (!confirmDmpWrite(t('tickets:dmpLookup.swap.confirmAction', { a: a.jvid, b: b.jvid }))) return;
    setSaving(true);
    setError(null);
    try {
      const res = await swapBusNumbers(a.jvid, b.jvid);
      toast.success(t('tickets:dmpLookup.swap.done', {
        a: res.data.a.jvid, aBox: box(res.data.a.to), b: res.data.b.jvid, bBox: box(res.data.b.to),
      }));
      onSwapped(a.jvid);
    } catch (err) {
      setError((err as Error)?.message || t('tickets:dmpLookup.swap.error'));
    } finally {
      setSaving(false);
    }
  };

  const mappedFor = (jvid: number) => mapped.filter((m) => m.jvid === jvid && m.busNumber);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px] animate-in fade-in duration-150"
      onClick={() => { if (!saving) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('tickets:dmpLookup.swap.title')}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl"
      >
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <ArrowLeftRight className="h-4 w-4 text-brand-rose" />
            {t('tickets:dmpLookup.swap.title')}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">{t('tickets:dmpLookup.swap.subtitle')}</p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Side A is the loaded address — nothing to choose, so nothing to get wrong. */}
          <div className="rounded-xl border border-slate-200 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {t('tickets:dmpLookup.swap.sideA')}
            </p>
            <p className="text-sm text-slate-800">{addressOf(a)}</p>
            <p className="text-[11px] text-slate-500">
              JVID {a.jvid} · {t('tickets:validation.fields.busNumber')} <strong>{box(a.busNumber)}</strong>
            </p>
          </div>

          <div>
            <label className="text-xs text-slate-500" htmlFor="swap-other-jvid">
              {t('tickets:dmpLookup.swap.otherJvid')}
            </label>
            <div className="mt-1 flex gap-2">
              <Input
                id="swap-other-jvid"
                value={input}
                inputMode="numeric"
                placeholder={t('tickets:dmpLookup.swap.otherJvidPlaceholder')}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '');
                  setInput(next);
                  // Drop a loaded side B as soon as the field stops naming it: the table below must
                  // never describe one address while the field shows another.
                  if (other && String(other.reference.jvid) !== next) { setOther(null); setMapped([]); setPastSwaps([]); }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') void loadOther(); }}
                disabled={loading || saving}
                autoComplete="new-password"
                name="dmp-swap-b"
                data-1p-ignore
                data-lpignore="true"
                className="h-8 text-sm"
              />
              <Button size="sm" variant="outline" onClick={() => void loadOther()} disabled={loading || saving || !/^\d+$/.test(input.trim())}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {error && (
            <p className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800" role="alert">
              {error}
            </p>
          )}

          {b && (
            <>
              {/* First, because it is the one warning about THIS gesture rather than about the data: the
                  same two addresses were already swapped (user ask 2026-09-15). Informs, never blocks. */}
              {pastSwaps && pastSwaps.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2" role="note">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                  <div className="space-y-1 text-xs text-rose-800">
                    <p className="font-semibold">{t('tickets:dmpLookup.swap.alreadySwapped', { count: pastSwaps.length })}</p>
                    <ul className="space-y-0.5">
                      {pastSwaps.map((s, i) => (
                        <li key={`${s.swappedAt}-${i}`}>
                          {t('tickets:dmpLookup.swap.alreadySwappedLine', {
                            date: new Date(s.swappedAt).toLocaleString(),
                            who: s.by ?? '?',
                            a: a.jvid, aFrom: box(s.aFrom), aTo: box(s.aTo),
                            b: b.jvid, bFrom: box(s.aTo), bTo: box(s.aFrom),
                          })}
                        </li>
                      ))}
                    </ul>
                    {undoesLastSwap && <p>{t('tickets:dmpLookup.swap.undoesLastSwap')}</p>}
                  </div>
                </div>
              )}
              {pastSwaps === null && (
                <div className="flex items-start gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2" role="note">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                  <span className="text-xs text-slate-700">{t('tickets:dmpLookup.swap.swapHistoryUnavailable')}</span>
                </div>
              )}
              {differentBuilding && (
                <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2" role="note">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                  <span className="text-xs text-rose-800">{t('tickets:dmpLookup.swap.differentBuilding')}</span>
                </div>
              )}
              {connected && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" role="note">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span className="text-xs text-amber-800">{t('tickets:dmpLookup.swap.connectedWarning')}</span>
                </div>
              )}
              {identicalBoxes && (
                <div className="flex items-start gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2" role="note">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                  <span className="text-xs text-slate-700">{t('tickets:dmpLookup.swap.identicalBoxes')}</span>
                </div>
              )}

              {/* The proposal itself. Two rows, current box → proposed box; the admin validates a
                  computed result instead of retyping two numbers. */}
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">{t('tickets:dmpLookup.swap.colAddress')}</th>
                      <th className="px-3 py-2 text-left font-semibold">{t('tickets:dmpLookup.swap.colCurrent')}</th>
                      <th className="px-3 py-2 text-left font-semibold">{t('tickets:dmpLookup.swap.colNext')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[[a, b], [b, a]].map(([from, to]) => (
                      <tr key={from.jvid} className="border-t border-slate-100">
                        <td className="px-3 py-2">
                          <span className="text-slate-800">{addressOf(from)}</span>
                          <span className="ml-1 text-slate-400">· JVID {from.jvid}</span>
                          {mappedFor(from.jvid).length > 0 && (
                            <span className="block text-[10px] text-slate-400">
                              {t('tickets:dmpLookup.swap.operatorBoxes', {
                                list: mappedFor(from.jvid).map((m) => `${m.operatorLabel} ${m.busNumber}`).join(', '),
                              })}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-rose-700">{box(from.busNumber)}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1.5 font-mono text-emerald-700">
                            <ArrowRight className="h-3 w-3 text-brand-rose" />
                            {box(to.busNumber)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Said plainly rather than left to be discovered: the operators' own numbering is shown
                  above but is NOT rewritten — that table is their submission, not our referential. */}
              {mapped.length > 0 && (
                <p className="text-[11px] text-slate-400">{t('tickets:dmpLookup.swap.mappingUntouched')}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <Button size="sm" variant="outline" onClick={onClose} disabled={saving}>
            <X className="mr-1.5 h-4 w-4" />
            {t('tickets:dmpLookup.swap.cancel')}
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={!canSwap}>
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ArrowLeftRight className="mr-1.5 h-4 w-4" />}
            {t('tickets:dmpLookup.swap.confirm')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
